import axios from "axios";
import { get, upperFirst } from "lodash";
import Vue from "vue";
import Router from "vue-router";
import { removeCookie } from "tiny-cookie";

import store from "../store";
import { fromBase64 } from "@/lib/stringUtil";

import GenericApp from "../views/GenericApp.vue";

// Build
import BuildApp from "../views/build/App.vue";

// Export
import BuildExportBilling from "../views/build/export/Billing.vue";
import BuildExportClinicInformation from "../views/build/export/BillingClinicInformation.vue";
import BuildExportBillingTrizetto from "../views/build/export/BillingTrizetto.vue";
import BuildExportBillingPatientStatement from "../views/build/export/BillingPatientStatement.vue";

import BuildExportEpicMappingList from "@/components/build/export/epicMapping/List";
import BuildExportEpicMappingPreview from "@/components/build/export/epicMapping/Preview";

import BuildExportStructure from "../views/build/export/Structure.vue";

// Structure
import StructureApp from "../views/build/structure/App.vue";
import StructureChart from "../views/build/structure/Chart.vue";
import StructureDepartmentList from "../views/build/structure/DepartmentList.vue";
import StructureList from "../views/build/structure/List.vue";
import StructureLocationForm from "../views/build/structure/LocationForm.vue";
import StructureOrganizationForm from "../views/build/structure/OrganizationForm.vue";

import BuildWrapper from "../views/build/entity/App.vue";
import BuildEdit from "../views/build/entity/Edit.vue";
import BuildEditBatch from "../views/build/entity/EditBatch.vue";
import BuildView from "../views/build/entity/View.vue";
import BuildImportFile from "../views/build/entity/ImportFile.vue";
import BuildList from "../views/build/entity/List.vue";

import Intro from "../views/build/Intro.vue";

// Organization User Security
import OrganizationRoleList from "../views/build/securityRole/List.vue";
import OrganizationRoleForm from "../views/build/securityRole/Form.vue";

// Ochin Pages
import OchinApp from "../views/ochin/OchinApp.vue";
import OchinLocation from "../views/ochin/location/App.vue";
import OchinMenu from "../views/ochin/Menu.vue";
import OchinOrganizationForm from "../views/ochin/organization/Form.vue";
import OchinOrganizationList from "../views/ochin/organization/List.vue";

import AdminLanding from "@/components/admin/Landing.vue";
import AdminDetail from "@/components/admin/Detail.vue";
import AdminList from "@/components/admin/List.vue";

import UserSecurityApp from "../views/ochin/userSecurity/App.vue";
import UserSecurityAnswerForm from "../views/ochin/userSecurity/question/answer/Form.vue";
import UserSecurityFunctionForm from "../views/ochin/userSecurity/function/Form.vue";
import UserSecurityFunctionList from "../views/ochin/userSecurity/function/List.vue";
import UserSecurityPrimaryRoleForm from "../views/ochin/userSecurity/primaryRole/Form.vue";
import UserSecurityPrimaryRoleList from "../views/ochin/userSecurity/primaryRole/List.vue";
import UserSecurityQuestionForm from "../views/ochin/userSecurity/question/Form.vue";
import UserSecurityQuestionList from "../views/ochin/userSecurity/question/List.vue";

import { userRoute } from "./userRoute";

Vue.use(Router);

const compassRoseEllaUrl =
  "https://ella.ochin.org/ip/mod/data/view.php?d=1&rid=1959";

function checkPermission(permission) {
  const {
    state: { session },
  } = store;

  if (!session.loggedIn) return false;

  const permissionValue = get(session.user.permission, permission);
  if (!permissionValue) {
    router.app.$notifyError({
      message:
        "User does not have permission to access page. Redirecting to home page.",
    });
  }
  return !!permissionValue;
}

function getCurrentOrgId() {
  const {
    state: { session },
  } = store;

  const id = session.organizationId;

  if (id === null || id === undefined) {
    throw new Error(
      `Failed to lookup session organizationId (got value ${id})`
    );
  }

  return id;
}

async function checkSession(to, from, next) {
  try {
    await store.dispatch("session/loadSession");

    const {
      state: {
        session: { loggedIn },
      },
    } = store;

    const { updateOrganizationId } = to.query;

    if (loggedIn && updateOrganizationId) {
      // Update session with organizationId in query param
      await axios({
        method: "put",
        url: `/api/session/${updateOrganizationId}`,
      });

      // Refresh store with new organizationId
      await store.dispatch("session/loadSession");

      // Remove query param to prevent infinite loop
      delete to.query.updateOrganizationId;

      next(to);
      return;
    }

    if (!loggedIn) {
      removeCookie("jwt");
      next({ name: "login", query: { redirect: to.fullPath } });
    }
    return;
  } catch (e) {
    removeCookie("jwt");
    next({ name: "login", query: { redirect: to.fullPath } });
  }
}

const buildConfig = [
  {
    path: "intro",
    name: "intro",
    component: Intro,
    meta: { breadcrumb: "Intro", title: "Introduction" },
  },
  {
    component: BuildWrapper,
    meta: { title: "Compass Rose", breadcrumb: "Compass Rose" },
    name: "compassRoseBuild",
    path: "compass-rose",
    redirect: { name: "compassRoseView" },
    children: [
      {
        component: BuildEdit,
        meta: {
          title: "Compass Rose Edit",
          breadcrumb: "Edit",
        },
        name: "compassRoseEdit",
        path: "edit",
        props: () => {
          return {
            entityName: "organization",
            id: getCurrentOrgId(),
            infoAlertHTML: `Before filling this section, please explore our <a href='${compassRoseEllaUrl}' target="_blank">Compass Rose Ella Course Collection</a>.`,
            redirectOverride: "compassRoseView",
            showTimestampHeader: false,
          };
        },
      },
      {
        component: BuildView,
        meta: {
          title: "Compass Rose",
          breadcrumb: "View",
        },
        name: "compassRoseView",
        path: "view",
        props: () => {
          return {
            editRedirect: "compassRoseEdit",
            entityName: "organization",
            id: getCurrentOrgId(),
            infoAlertHTML: `Before filling this section, please explore our <a href='${compassRoseEllaUrl}' target="_blank">Compass Rose Ella Course Collection</a>.`,
            showTimestampHeader: false,
          };
        },
      },
    ],
  },
  {
    path: "structure",
    name: "buildStructureApp",
    component: StructureApp,
    meta: {
      breadcrumb: "Structure",
    },
    redirect: {
      name: "structureChart",
    },
    children: [
      {
        path: "organization",
        name: "structureOrganizationForm",
        component: StructureOrganizationForm,
        beforeEnter(to, from, next) {
          if (checkPermission("structureApp.updateOrganization")) {
            return next();
          }
          next("/");
        },
        meta: {
          breadcrumb: "Edit Organization",
          title: "Organization Form | Structure",
        },
      },
      {
        path: "chart",
        name: "structureChart",
        component: StructureChart,
        meta: {
          breadcrumb: "Chart View",
          title: "Structure (Chart View)",
        },
      },
      {
        path: "list",
        name: "structureList",
        component: StructureList,
        meta: {
          breadcrumb: "Table View",
          title: "Structure (Table View)",
        },
      },
      {
        path: "location",
        name: "structureLocation",
        component: GenericApp,
        meta: {
          breadcrumb: "Location",
        },
        redirect: {
          name: "structureList",
        },
        children: [
          {
            path: "create",
            name: "structureLocationCreate",
            component: StructureLocationForm,
            meta: {
              breadcrumb: "Create",
              title: "Create Location | Structure",
            },
          },
          {
            path: ":locationId",
            name: "structureLocationItemApp",
            component: GenericApp,
            redirect: {
              name: "structureLocationForm",
            },
            children: [
              {
                path: "edit",
                name: "structureLocationForm",
                component: StructureLocationForm,
                meta: {
                  breadcrumb: "Edit",
                  title: "Edit Location | Structure",
                },
              },
              {
                path: "department",
                name: "structureDepartmentApp",
                component: GenericApp,
                meta: {
                  breadcrumb: "Department",
                },
                redirect: {
                  name: "structureDepartmentList",
                },
                children: [
                  {
                    path: "list",
                    name: "structureDepartmentList",
                    component: StructureDepartmentList,
                    meta: {
                      title: "Department List | Structure",
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  // User Security - Organization Role
  {
    component: GenericApp,
    meta: { title: "Security Roles", breadcrumb: "Security Roles" },
    name: "organizationRoleApp",
    path: "role",
    redirect: {
      name: "organizationRoleList",
    },
    children: [
      {
        meta: { title: "Role List | Security Roles", breadcrumb: "List" },
        component: OrganizationRoleList,
        name: "organizationRoleList",
        path: "list",
      },
      {
        component: OrganizationRoleForm,
        meta: {
          title: "Create Role | Security Roles",
          breadcrumb: "Create",
        },
        name: "organizationRoleCreate",
        path: "create",
      },
      {
        component: GenericApp,
        name: "organizationRoleIdApp",
        path: ":id",
        children: [
          {
            meta: {
              title: "Edit Role | Security Roles",
              breadcrumb: "Edit",
            },
            component: OrganizationRoleForm,
            name: "organizationRoleEdit",
            path: "edit",
            props: true,
          },
        ],
      },
    ],
  },
  // Build Entity
  userRoute(),
  buildEntityRouteFactory("resource"),
  buildEntityRouteFactory("department"),
  {
    component: GenericApp,
    name: "export",
    path: "export",
    meta: {
      breadcrumb: "Exports",
    },
    redirect: {
      name: "exportBilling",
    },
    async beforeEnter(to, from, next) {
      await checkSession(to, from, next);
      if (!store.state.session.user.isOchinUser) {
        next("/build");
        return;
      }
      next();
    },
    children: [
      {
        component: BuildExportStructure,
        name: "exportStructure",
        meta: {
          breadcrumb: "Structure",
          title: "Structure | Exports",
        },
        path: "export/structure",
        props: { title: "Structure" },
      },
      {
        component: GenericApp,
        name: "exportBilling",
        path: "billing",
        meta: {
          breadcrumb: "Billing",
        },
        redirect: {
          name: "exportBillingLanding",
        },
        children: [
          {
            component: BuildExportBilling,
            name: "exportBillingLanding",
            path: "landing",
            meta: {
              breadcrumb: "List",
              title: "Billing | Exports",
            },
          },
          {
            component: BuildExportClinicInformation,
            name: "exportBillingClinicInformation",
            path: "clinicInformation",
            meta: {
              breadcrumb: "Clinic Information",
              title: "Clinic Information | Exports",
            },
          },
          {
            component: BuildExportBillingTrizetto,
            name: "exportBillingTrizetto",
            path: "trizetto",
            meta: {
              breadcrumb: "Trizetto",
              title: "Trizetto | Exports",
            },
          },
          {
            component: BuildExportBillingPatientStatement,
            name: "exportBillingPatientStatement",
            path: "patientStatement",
            meta: {
              breadcrumb: "Patient Statements",
              title: "Billing Patient Statement | Exports",
            },
          },
        ],
      },
      {
        component: BuildExportEpicMappingList,
        meta: {
          breadcrumb: "User",
          title: "User | Exports",
        },
        name: "exportUser",
        path: "user",
        props: { entityName: "user", title: "User" },
      },
      {
        component: BuildExportEpicMappingList,
        meta: {
          breadcrumb: "Resources",
          title: "Resources | Exports",
        },
        name: "exportResource",
        path: "resource",
        props: { entityName: "resource", title: "Resource" },
      },
    ],
  },
].filter(config => {
  if (config.name === "buildMemberApp") {
    return store.state.featureFlag.memberAdmin;
  }

  return true;
});

const routeConfig = {
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      name: "home",
      redirect: { name: "intro" },
      async beforeEnter(to, from, next) {
        await checkSession(to, from, next);
        next();
      },
    },
    {
      path: "/build",
      name: "buildApp",
      meta: { breadcrumb: "Build" },
      component: BuildApp,
      redirect: {
        name: "intro",
      },
      async beforeEnter(to, from, next) {
        await checkSession(to, from, next);
        next();
      },
      children: [...buildConfig],
    },
    {
      async beforeEnter(to, from, next) {
        await checkSession(to, from, next);
        next();
      },
      component: BuildExportEpicMappingPreview,
      meta: {
        title: "User Preview | Exports",
      },
      name: "userEpicMappingPreview",
      path: "/build/export/user/:id",
      props: route => {
        let {
          params: { id },
        } = route;

        id = parseInt(id, 10);

        return { entityName: "user", id, title: "User Export" };
      },
    },
    {
      async beforeEnter(to, from, next) {
        await checkSession(to, from, next);
        next();
      },
      component: BuildExportEpicMappingPreview,
      meta: {
        title: "Resource Preview | Exports",
      },
      name: "resourceEpicMappingPreview",
      path: "/build/export/resource/:id",
      props: route => {
        let {
          params: { id },
        } = route;

        id = parseInt(id, 10);

        return { entityName: "resource", id, title: "User Export" };
      },
    },
    {
      path: "/ochin",
      name: "ochinHome",
      component: OchinApp,
      meta: {
        breadcrumb: "Admin",
      },
      redirect: { name: "ochinMenu" },
      async beforeEnter(to, from, next) {
        await checkSession(to, from, next);
        const adminAppPermission =
          store.state.session.user.permission.adminApp.view;
        if (!adminAppPermission) {
          next("/");
        }
        next();
      },
      children: [
        {
          path: "menu",
          name: "ochinMenu",
          component: OchinMenu,
          meta: {
            title: "OCHIN Menu",
          },
        },
        {
          path: "location",
          name: "ochinLocation",
          component: OchinLocation,
          meta: {
            title: "Import Locations | OCHIN Menu",
          },
        },
        {
          path: "organization",
          name: "ochinOrganizationApp",
          component: GenericApp,
          meta: {
            breadcrumb: "Organizations",
          },
          redirect: {
            name: "ochinOrganizationList",
          },
          children: [
            {
              path: "create",
              name: "ochinOrganizationFormCreate",
              component: OchinOrganizationForm,
              meta: {
                breadcrumb: "Create",
                title: "Create Organization | OCHIN Menu",
              },
            },
            {
              path: "list",
              name: "ochinOrganizationList",
              component: OchinOrganizationList,
              meta: {
                breadcrumb: "List",
                title: "Organization | OCHIN Menu",
              },
            },
            {
              path: ":organizationId",
              name: "ochinOrganizationItemApp",
              component: GenericApp,
              children: [
                {
                  path: "edit",
                  name: "ochinOrganizationFormEdit",
                  component: OchinOrganizationForm,
                  meta: {
                    breadcrumb: "Edit",
                    title: "Edit Organization | OCHIN Menu",
                  },
                },
              ],
            },
          ],
        },
        {
          path: "userSecurity",
          name: "ochinUserSecurity",
          component: UserSecurityApp,
          meta: {
            breadcrumb: "User Security",
          },
          redirect: {
            name: "ochinUserSecurityPrimaryRoleList",
          },
          children: [
            {
              path: "function",
              name: "ochinUserSecurityFunction",
              component: GenericApp,
              meta: {
                breadcrumb: "Security Functions",
              },
              redirect: {
                name: "ochinUserSecurityFunctionList",
              },
              children: [
                {
                  path: "list",
                  name: "ochinUserSecurityFunctionList",
                  component: UserSecurityFunctionList,
                  meta: {
                    breadcrumb: "List",
                    title: "Security Functions | User Security",
                  },
                },
                {
                  path: "create",
                  name: "ochinUserSecurityFunctionFormCreate",
                  component: UserSecurityFunctionForm,
                  meta: {
                    breadcrumb: "Create",
                    title: "Create Security Function | User Security",
                  },
                },
                {
                  path: ":functionId",
                  name: "ochinUserSecurityFunctionApp",
                  component: GenericApp,
                  redirect: {
                    name: "ochinUserSecurityFunctionForm",
                  },
                  meta: {
                    breadcrumb: "(Security Function)",
                  },
                  children: [
                    {
                      path: "form",
                      name: "ochinUserSecurityFunctionForm",
                      component: UserSecurityFunctionForm,
                      meta: {
                        breadcrumb: "Edit",
                        title: "Edit Security Function | User Security",
                      },
                    },
                  ],
                },
              ],
            },
            {
              path: "primaryRole",
              name: "ochinUserSecurityPrimaryRole",
              component: GenericApp,
              meta: {
                breadcrumb: "Primary Roles",
              },
              redirect: {
                name: "ochinUserSecurityPrimaryRoleList",
              },
              children: [
                {
                  path: "list",
                  name: "ochinUserSecurityPrimaryRoleList",
                  component: UserSecurityPrimaryRoleList,
                  meta: {
                    breadcrumb: "List",
                    title: "Primary Roles | User Security",
                  },
                },
                {
                  path: "create",
                  name: "ochinUserSecurityPrimaryRoleFormCreate",
                  component: UserSecurityPrimaryRoleForm,
                  meta: {
                    breadcrumb: "Create",
                    title: "Create Primary Role | User Security",
                  },
                },
                {
                  path: ":primaryRoleId",
                  name: "ochinUserSecurityPrimaryRoleRecordApp",
                  component: GenericApp,
                  redirect: {
                    name: "ochinUserSecurityPrimaryRoleForm",
                  },
                  meta: {
                    breadcrumb: "(Primary Role)",
                  },
                  children: [
                    {
                      path: "form",
                      name: "ochinUserSecurityPrimaryRoleForm",
                      component: UserSecurityPrimaryRoleForm,
                      meta: {
                        breadcrumb: "Edit",
                        title: "Edit Primary Role | User Security",
                      },
                    },
                    {
                      path: "question",
                      name: "ochinUserSecurityPrimaryRoleQuestionApp",
                      component: GenericApp,
                      redirect: {
                        name: "ochinUserSecurityPrimaryRoleForm",
                      },
                      meta: {
                        breadcrumb: "Questions",
                      },
                    },
                  ],
                },
              ],
            },
            {
              path: "question",
              name: "ochinUserSecurityQuestionApp",
              component: GenericApp,
              redirect: {
                name: "ochinUserSecurityQuestionList",
              },
              meta: {
                breadcrumb: "Questions",
              },
              children: [
                {
                  path: "create",
                  name: "ochinUserSecurityQuestionCreate",
                  component: UserSecurityQuestionForm,
                  meta: {
                    breadcrumb: "Create",
                    title: "Create Question | User Security",
                  },
                },
                {
                  path: "list",
                  name: "ochinUserSecurityQuestionList",
                  component: UserSecurityQuestionList,
                  meta: {
                    breadcrumb: "List",
                    title: "Questions | User Security",
                  },
                },
                {
                  path: ":questionId",
                  name: "ochinUserSecurityQuestionRecordApp",
                  component: GenericApp,
                  redirect: {
                    name: "ochinUserSecurityQuestionForm",
                  },
                  meta: {
                    breadcrumb: "(Question)",
                  },
                  children: [
                    {
                      path: "form",
                      name: "ochinUserSecurityQuestionForm",
                      component: UserSecurityQuestionForm,
                      meta: {
                        breadcrumb: "Edit",
                        title: "Edit Question | User Security",
                      },
                    },
                    {
                      path: "answer",
                      name: "ochinUserSecurityAnswerApp",
                      component: GenericApp,
                      redirect: {
                        name: "ochinUserSecurityQuestionForm",
                      },
                      meta: {
                        breadcrumb: "Answers",
                      },
                      children: [
                        {
                          path: "create",
                          name: "ochinUserSecurityAnswerCreate",
                          component: UserSecurityAnswerForm,
                          meta: {
                            breadcrumb: "Create",
                            title: "Create Answer | User Security",
                          },
                        },
                        {
                          path: ":answerId",
                          name: "ochinUserSecurityAnswerFormApp",
                          component: GenericApp,
                          redirect: {
                            name: "ochinUserSecurityQuestionForm",
                          },
                          meta: {
                            breadcrumb: "(Answer)",
                          },
                          children: [
                            {
                              path: "form",
                              name: "ochinUserSecurityAnswerForm",
                              component: UserSecurityAnswerForm,
                              meta: {
                                breadcrumb: "Edit",
                                title: "Edit Answer | User Security",
                              },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        adminEntityRouteFactory("user"),
      ],
    },
  ],
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
};

const router = new Router(routeConfig);

router.beforeEach((to, from, next) => {
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find(route => route.meta && route.meta.title);
  if (nearestWithTitle)
    document.title = `${nearestWithTitle.meta.title} | OCHIN Valet`;
  next();
});

axios.interceptors.response.use(null, e => {
  if (e.response.status === 401) {
    console.error("User is not authenticated. Redirecting to /login...");
    removeCookie("jwt");
    removeCookie("jwt", { domain: "ochin.info" });

    store.dispatch("session/logout");

    const routeWhiteList = [
      "login",
      "memberConfirmAccount",
      "memberCreateAccount",
      "memberPendingConfirmation",
    ];

    if (!routeWhiteList.includes(router.currentRoute.name)) {
      location.href = "/login";
    }
  }
  throw e;
});

function adminEntityRouteFactory(entityName) {
  const entityNameUpperFirst = upperFirst(entityName);
  const entityNameUpperFirstPlural = `${upperFirst(entityName)}s`;

  return {
    component: AdminLanding,
    meta: {
      breadcrumb: entityNameUpperFirstPlural,
      title: entityNameUpperFirstPlural,
    },
    name: `ochin${entityNameUpperFirst}`,
    path: entityName,
    props: { routeName: `ochin${entityNameUpperFirst}` },
    redirect: { name: `ochin${entityNameUpperFirst}Attribute` },
    children: [
      {
        component: AdminList,
        meta: {
          breadcrumb: "Attribute",
          title: "Attribute",
        },
        name: `ochin${entityNameUpperFirst}Attribute`,
        path: "attribute",
        props: () => {
          return {
            entityName: `${entityName}Attribute`,
            routeName: `ochin${entityNameUpperFirst}Attribute`,
            parentEntityName: entityName,
          };
        },
        children: [
          {
            component: AdminDetail,
            meta: {
              breadcrumb: "Create",
              title: `Create ${entityNameUpperFirst} Attribute`,
            },
            name: `ochin${entityNameUpperFirst}AttributeCreate`,
            path: "create",
            props: () => {
              return {
                entityName: `${entityName}Attribute`,
                mode: "create",
                routeName: `ochin${entityNameUpperFirst}AttributeCreate`,
              };
            },
          },
          {
            component: AdminDetail,
            meta: {
              breadcrumb: "name",
              breadcrumbIsParam: true,
              title: `Edit ${entityNameUpperFirst} Attribute`,
            },
            name: `ochin${entityNameUpperFirst}AttributeEdit`,
            path: ":name",
            props: route => {
              const { params } = route;
              const { name } = params;

              return {
                entityName: `${entityName}Attribute`,
                id: name,
                mode: "update",
                routeName: `ochin${entityNameUpperFirst}AttributeEdit`,
              };
            },
          },
        ],
      },
      {
        component: AdminList,
        meta: { breadcrumb: "Presets", title: "Presets" },
        name: `ochin${entityNameUpperFirst}Preset`,
        path: "preset",
        props: () => {
          return {
            entityName: `${entityName}Preset`,
            routeName: `ochin${entityNameUpperFirst}Preset`,
          };
        },
        children: [
          {
            component: AdminDetail,
            meta: { breadcrumb: "Create" },
            name: `ochin${entityNameUpperFirst}PresetCreate`,
            path: "create",
            props: () => {
              return {
                entityName: `${entityName}Preset`,
                routeName: `ochin${entityNameUpperFirst}PresetCreate`,
              };
            },
          },
          {
            component: AdminDetail,
            meta: {
              breadcrumb: "id",
              breadcrumbIsParam: true,
              title: `Edit ${entityNameUpperFirst} Form`,
            },
            name: `ochin${entityNameUpperFirst}PresetEdit`,
            path: ":id",
            props: route => {
              const { params } = route;
              let { id } = params;

              id = parseInt(id, 10);

              return {
                entityName: `${entityName}Preset`,
                id,
                mode: "update",
                routeName: `ochin${entityNameUpperFirst}PresetEdit`,
              };
            },
          },
        ],
      },
      {
        component: AdminList,
        meta: { breadcrumb: "Mappings", title: "Epic Mappings" },
        name: `ochin${entityNameUpperFirst}EpicMapping`,
        path: "mapping",
        props: () => {
          return {
            entityName: `${entityName}EpicMapping`,
            routeName: `ochin${entityNameUpperFirst}EpicMapping`,
          };
        },
        children: [
          {
            component: AdminDetail,
            meta: { breadcrumb: "Create" },
            name: `ochin${entityNameUpperFirst}EpicMappingCreate`,
            path: "create",
            props: () => {
              return {
                entityName: `${entityName}EpicMapping`,
                routeName: `ochin${entityNameUpperFirst}EpicMappingCreate`,
              };
            },
          },
          {
            component: AdminDetail,
            meta: { breadcrumb: "id", breadcrumbIsParam: true },
            name: `ochin${entityNameUpperFirst}EpicMappingEdit`,
            path: ":id",
            props: route => {
              const { params } = route;
              let { id } = params;

              id = parseInt(id, 10);

              return {
                entityName: `${entityName}EpicMapping`,
                id,
                mode: "update",
                routeName: `ochin${entityNameUpperFirst}EpicMappingEdit`,
              };
            },
          },
        ],
      },
    ],
  };
}

function buildEntityRouteFactory(entityName) {
  const entityNameUpperFirst = upperFirst(entityName);
  const entityNameUpperFirstPlural = `${upperFirst(entityName)}s`;

  return {
    component: BuildWrapper,
    meta: { title: entityNameUpperFirst, breadcrumb: entityNameUpperFirst },
    name: `${entityName}Build`,
    path: entityName,
    redirect: { name: `${entityName}List` },
    children: [
      {
        component: BuildEdit,
        meta: {
          title: `Create ${entityNameUpperFirst} | ${entityNameUpperFirstPlural}`,
          breadcrumb: "Create",
        },
        name: `${entityName}Create`,
        path: "create",
        props: { entityName },
      },
      {
        component: BuildEdit,
        meta: {
          title: `Edit ${entityNameUpperFirst} | ${entityNameUpperFirstPlural}`,
          breadcrumb: "Edit",
        },
        name: `${entityName}Edit`,
        path: "edit/:id",
        props: route => {
          let {
            params: { id },
          } = route;

          id = parseInt(id, 10);

          return { entityName, id };
        },
      },
      {
        component: BuildEditBatch,
        meta: {
          title: `Batch Edit ${entityNameUpperFirst} | ${entityNameUpperFirstPlural}`,
          breadcrumb: `Edit (${entityNameUpperFirstPlural})`,
        },
        name: `${entityName}EditBatch`,
        path: "edit-batch/:idListEncoded",
        props: route => {
          const {
            params: { idListEncoded },
          } = route;

          const idListString = fromBase64(idListEncoded);
          const idList = idListString.split(",");

          return { entityName, idList };
        },
      },
      {
        component: BuildList,
        name: `${entityName}List`,
        meta: {
          breadcrumb: "List",
          title: `${entityNameUpperFirst} List | ${entityNameUpperFirstPlural}`,
        },
        path: "list",
        props: route => {
          const {
            query: { filterMode },
          } = route;

          return { entityName, filterMode: parseInt(filterMode, 10) };
        },
      },
      {
        component: BuildImportFile,
        meta: {
          title: `Upload Instructions | ${entityNameUpperFirstPlural}`,
          breadcrumb: "Import",
        },
        name: `${entityName}ImportFile`,
        path: "import-file",
        props: {
          entityName,
        },
      },
      {
        component: BuildImportFile,
        meta: {
          title: `Import Confirm | ${entityNameUpperFirstPlural}`,
          breadcrumb: "Import Confirm",
        },
        name: `${entityName}ImportFileConfirm`,
        path: "import-file/:fileId",
        props: route => {
          let {
            params: { fileId },
          } = route;

          fileId = parseInt(fileId, 10);

          return { entityName, fileId };
        },
      },
    ],
  };
}

export default router;
